import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Title, Tabs, Metric, Table, Spinner, Button, Selectbox, Message, useToast, Modal, DateInput, NumberInput } from './bpm';
import { usePortfolio } from '../contexts/PortfolioContextIndex';
import { useAuth } from '../contexts/AuthContext';
import { assetEnrichmentAPI, categoriesAPI, twsAPI } from '../services/api';
import api from '../services/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { portfolioAPI } from '../services/api';
import { getPageIcon } from '../utils/pageIcons';
import { usePageRenderTime, logDataLoad } from '../utils/performanceLogger';
import './Portefeuille.css';
import './Parametres.css';
import './Compte.css'; /* même mise en page que le graphique Évolution de la valeur du compte */

const Portefeuille = ({ onActifClick, defaultTab = 0, onLogoClick = null }) => {
  usePageRenderTime('Portefeuille');
  const [activeTab, setActiveTab] = useState(defaultTab);
  const { profile } = useAuth();
  const { 
    isConnected, 
    account, 
    positions, 
    loading, 
    backgroundLoading,
    loadData, 
    loadPositions 
  } = usePortfolio();
  
  const { showToast } = useToast();

  const canAccessPortefeuilleTab = useCallback((tabId) => {
    if (!profile) return true;
    if (profile.is_superadmin || profile.is_owner) return true;
    const raw = profile.allowed_tabs;
    const allowed = Array.isArray(raw) ? raw : [];
    if (allowed.length === 0) return true;
    return allowed.includes(tabId);
  }, [profile?.allowed_tabs, profile?.is_superadmin, profile?.is_owner]);
  
  // États pour les catégories et données d'enrichissement
  // Initialiser depuis sessionStorage si disponible pour un chargement instantané
  const [categories, setCategories] = useState(() => {
    try {
      const savedCats = sessionStorage.getItem('portefeuille_categories');
      const savedCatsTimestamp = sessionStorage.getItem('portefeuille_categories_timestamp');
      if (savedCats && savedCatsTimestamp) {
        const age = Date.now() - parseInt(savedCatsTimestamp, 10);
        if (age < 5 * 60 * 1000) { // Moins de 5 minutes
          const cats = JSON.parse(savedCats);
          return cats.filter(cat => cat.is_active);
        }
      }
    } catch (e) {
      // Ignorer les erreurs de sessionStorage
    }
    return [];
  });
  const [enrichmentMap, setEnrichmentMap] = useState(new Map()); // Map symbol -> category name
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // État pour le résumé du compte (cash disponible, etc.)
  const [accountSummary, setAccountSummary] = useState(null);
  const [loadingAccountSummary, setLoadingAccountSummary] = useState(false);
  
  // State pour persister la dernière valeur valide de P&L journalier
  const [lastValidDailyPnL, setLastValidDailyPnL] = useState(() => {
    // Récupérer la dernière valeur sauvegardée au chargement
    try {
      const saved = localStorage.getItem('ibkr_last_daily_pnl');
      if (saved) {
        const value = parseFloat(saved);
        if (!isNaN(value)) {
          return { value, currency: 'EUR' };
        }
      }
    } catch (error) {
      console.warn('⚠️ Erreur lors de la récupération du P&L sauvegardé:', error);
    }
    return null;
  });
  
  // Référence pour éviter la boucle infinie dans useEffect
  // Utilisée pour comparer les valeurs sans déclencher de re-render
  const lastValidDailyPnLRef = useRef(lastValidDailyPnL);
  
  // État pour les ordres
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  
  // Cache pour les ordres (évite de recharger à chaque clic sur l'onglet)
  const ordersCacheRef = useRef({
    data: [],
    timestamp: 0
  });
  const ORDERS_CACHE_DURATION_MS = 30000; // 30 secondes

  // États pour l'onglet Valorisations (date + valeur, même structure que Compte — label "Valeur de la Position")
  const [valorisations, setValorisations] = useState([]);
  const [loadingValorisations, setLoadingValorisations] = useState(false);
  const [showAddValorisationModal, setShowAddValorisationModal] = useState(false);
  const [showEditValorisationModal, setShowEditValorisationModal] = useState(false);
  const [editingValorisation, setEditingValorisation] = useState(null);
  const [valorisationForm, setValorisationForm] = useState({
    date_valorisation: new Date().toISOString().split('T')[0],
    valeur_totale: '',
  });

  // Cache pour les données de distribution des graphiques
  const distributionCacheRef = useRef({
    data: null,
    timestamp: 0,
    positionsHash: '',
    enrichmentMapHash: ''
  });

  // Évolution de la valeur du portefeuille (Synthèse) — source: valuation-evolution (use_valeur_nette=false = Valorisation position)
  const [valuationEvolutionPortefeuille, setValuationEvolutionPortefeuille] = useState(null);
  const [loadingValuationEvolution, setLoadingValuationEvolution] = useState(false);
  const valuationDateRange = useMemo(() => ({
    start: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  }), []);

  // Graphique « Evolution de la valeur du portefeuille » — même logique que Compte : mesure du conteneur avant rendu Recharts
  const evolutionChartContainerRef = useRef(null);
  const [isEvolutionChartVisible, setIsEvolutionChartVisible] = useState(false);
  const [evolutionChartContainerDims, setEvolutionChartContainerDims] = useState({ width: 0, height: 0 });
  const [evolutionChartKey, setEvolutionChartKey] = useState(0);
  const lastEvolutionChartDimsRef = useRef({ width: 0, height: 0 });
  const evolutionResizeTimeoutRef = useRef(null);

  // Dernier compte affiché : évite le flip spinner ↔ métriques quand /tws/account-summary ou /tws/account renvoie 503
  const lastAccountRef = useRef(null);
  const [displayAccountStable, setDisplayAccountStable] = useState(null);
  if (!isConnected) lastAccountRef.current = null;
  else if (account) lastAccountRef.current = account;
  const displayAccount = isConnected ? (account ?? lastAccountRef.current) : null;
  useEffect(() => {
    if (!isConnected) {
      setDisplayAccountStable(null);
      return;
    }
    if (account && typeof account === 'object') {
      setDisplayAccountStable(account);
    }
  }, [isConnected, account]);
  // Si on a des positions (graphiques déjà affichés) mais pas encore de compte (503 ou lent), afficher les cartes avec "-" au lieu du spinner
  const accountForMetrics = displayAccount ?? displayAccountStable ?? (isConnected && positions?.length > 0 ? {} : null);

  // Mettre à jour l'onglet actif si defaultTab change (navigation depuis Dashboard)
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    // Charger les données si connecté (utilise le cache si disponible)
    if (isConnected) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  useEffect(() => {
    // Recharger les positions si on est connecté et qu'on change d'onglet vers "Portefeuille"
    if (isConnected && activeTab === 1) {
      loadPositions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isConnected]);

  // Fonction pour charger les ordres (définie avant les useEffect qui l'utilisent)
  const loadOrders = useCallback(async () => {
    const t0 = performance.now();
    setLoadingOrders(true);
    try {
      const response = await twsAPI.getOrders();
      const ordersData = response.data || [];
      setOrders(ordersData);
      
      // Mettre à jour le cache
      ordersCacheRef.current = {
        data: ordersData,
        timestamp: Date.now()
      };
      
      // Logs de débogage désactivés
    } catch (error) {
      if (error.response?.status === 503) {
        // TWS non connecté - ne pas afficher de toast pour éviter le spam
        // Le message est déjà affiché dans l'interface
      } else if (error.response?.status === 404) {
        showToast('Vérifiez que le backend est démarré et que l\'API est accessible.', 'error', 3000, 'Erreur de connexion', 'Portefeuille', getPageIcon('Portefeuille'));
      } else {
        const errorMsg = error.message || 'Erreur lors du chargement des ordres';
        showToast(errorMsg, 'error', 3000, 'Erreur de chargement', 'Portefeuille', getPageIcon('Portefeuille'));
      }
      setOrders([]);
    } finally {
      setLoadingOrders(false);
      const duration = performance.now() - t0;
      logDataLoad('Portefeuille - Ordres', duration);
    }
  }, [showToast]);

  // Tableau Valorisations Portefeuille = Valeur de la Position uniquement (table portfolio_valorisations).
  // IMPORTANT: utiliser UNIQUEMENT GET /portfolio/valuations (getValuations), jamais getValeurNette (Compte = portfolio_valeur_nette).
  const loadValorisations = useCallback(async () => {
    setLoadingValorisations(true);
    try {
      const response = await portfolioAPI.getValuations();
      const list = response?.data;
      // Ne jamais afficher les données "Valeur Nette" (Compte) : si la réponse contient valeur_nette sans valeur_totale, rejeter.
      if (Array.isArray(list) && list.length > 0) {
        const first = list[0];
        if (first && 'valeur_nette' in first && (first.valeur_totale === undefined || first.valeur_totale === null)) {
          console.warn('[Portefeuille] Données Valeur Nette (Compte) reçues. Le tableau affiche la Valeur de la Position (GET /portfolio/valuations, table portfolio_valorisations).');
          setValorisations([]);
          setLoadingValorisations(false);
          return;
        }
      }
      setValorisations(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Erreur chargement valorisations:', error);
      showToast('Erreur lors du chargement des valorisations', 'error', 5000, null, 'Portefeuille', getPageIcon('Portefeuille'));
      setValorisations([]);
    } finally {
      setLoadingValorisations(false);
    }
  }, [showToast]);

  // Charger l'évolution de la valeur (valorisations) pour le graphique Synthèse — défini avant handleSavePositionSnapshot/handleDeletePositionSnapshot
  const loadValuationEvolutionPortefeuille = useCallback(async () => {
    setLoadingValuationEvolution(true);
    try {
      const orgId = profile?.organization_id ?? 1;
      const userId = profile?.user_id ?? 1;
      const res = await portfolioAPI.getValuationEvolution(
        valuationDateRange.start,
        valuationDateRange.end,
        orgId,
        userId,
        false // useValeurNette = false → Valorisation de la position (portfolio_valorisations), pas Valeur Nette (Compte)
      );
      if (res?.data) setValuationEvolutionPortefeuille(res.data);
      else setValuationEvolutionPortefeuille(null);
    } catch (err) {
      if (err?.response?.status !== 404) {
        console.error('Erreur chargement évolution valorisation (Portefeuille):', err);
      }
      setValuationEvolutionPortefeuille(null);
    } finally {
      setLoadingValuationEvolution(false);
    }
  }, [valuationDateRange, profile?.organization_id, profile?.user_id]);

  // Création/édition = table portfolio_valorisations (Valeur de la Position) via POST/PUT /portfolio/valuations — pas valeur-nette.
  const handleSaveValorisation = useCallback(async () => {
    if (!valorisationForm.valeur_totale) return;
    try {
      const data = {
        date_valorisation: valorisationForm.date_valorisation,
        valeur_totale: parseFloat(valorisationForm.valeur_totale),
      };
      if (editingValorisation) {
        await portfolioAPI.updateValuation(editingValorisation.id, data);
        showToast('Valorisation modifiée', 'success', 3000, null, 'Portefeuille', getPageIcon('Portefeuille'));
      } else {
        // POST /portfolio/valuations → portfolio_valorisations uniquement
        const createResponse = await portfolioAPI.createValuation(data);
        showToast('Valorisation créée', 'success', 3000, null, 'Portefeuille', getPageIcon('Portefeuille'));
        // Mise à jour optimiste : afficher la ligne créée tout de suite (backend la retourne)
        if (createResponse?.data) {
          setValorisations((prev) => {
            const next = [createResponse.data, ...prev];
            return next.sort((a, b) => new Date(b.date_valorisation) - new Date(a.date_valorisation));
          });
        }
      }
      setShowAddValorisationModal(false);
      setShowEditValorisationModal(false);
      setEditingValorisation(null);
      setValorisationForm({
        date_valorisation: new Date().toISOString().split('T')[0],
        valeur_totale: '',
      });
      // Recharger la liste pour être en phase avec la base (et mettre à jour si édition)
      await loadValorisations();
      await loadValuationEvolutionPortefeuille();
    } catch (error) {
      console.error('Erreur sauvegarde valorisation:', error);
      showToast(error.response?.data?.detail || 'Erreur lors de la sauvegarde', 'error', 5000, null, 'Portefeuille', getPageIcon('Portefeuille'));
    }
  }, [valorisationForm, editingValorisation, showToast, loadValorisations, loadValuationEvolutionPortefeuille]);

  const handleDeleteValorisation = useCallback(async (val) => {
    if (!window.confirm('Supprimer cette valorisation ?')) return;
    try {
      await portfolioAPI.deleteValuation(val.id);
      showToast('Valorisation supprimée', 'success', 3000, null, 'Portefeuille', getPageIcon('Portefeuille'));
      setShowEditValorisationModal(false);
      setEditingValorisation(null);
      await loadValorisations();
      await loadValuationEvolutionPortefeuille();
    } catch (error) {
      console.error('Erreur suppression valorisation:', error);
      showToast('Erreur lors de la suppression', 'error', 5000, null, 'Portefeuille', getPageIcon('Portefeuille'));
    }
  }, [showToast, loadValorisations, loadValuationEvolutionPortefeuille]);
  
  // Recharger les ordres quand on change d'onglet vers "Ordres" (vérifier le statut réel)
  useEffect(() => {
    if (activeTab === 5) {
      // Vérifier le statut réel avant de charger
      const checkAndLoad = async () => {
        try {
          // Vérifier le statut réel de TWS
          const statusResponse = await twsAPI.getStatus();
          const actuallyConnected = statusResponse.data?.is_connected ?? false;
          
          if (actuallyConnected) {
            const now = Date.now();
            const cacheAge = now - ordersCacheRef.current.timestamp;
            
            // Si le cache est récent (moins de 30 secondes), utiliser les données en cache
            if (cacheAge < ORDERS_CACHE_DURATION_MS && ordersCacheRef.current.data.length > 0) {
              setOrders(ordersCacheRef.current.data);
            } else {
              // Sinon, recharger depuis le serveur
              loadOrders();
            }
          } else {
            // TWS n'est pas connecté, vider les ordres
            setOrders([]);
          }
        } catch (err) {
          // En cas d'erreur (503, etc.), TWS n'est pas connecté
          if (err.response?.status === 503) {
            setOrders([]);
          }
        }
      };
      
      checkAndLoad();
    }
  }, [activeTab, loadOrders]);

  // Fonction de debug pour afficher TOUTES les données IBKR disponibles (désactivée pour réduire les logs)
  const debugAccountSummary = useCallback((accountSummaryData) => {
    // Logs de débogage désactivés pour réduire le spam dans la console
    return accountSummaryData;
  }, []);

  // Fonction pour calculer le P&L journalier (fallback si DailyPnL n'est pas disponible)
  const calculateDailyPnL = useCallback((currentNetLiquidation, accountSummaryData) => {
    // Debug complet des données disponibles (désactivé)
    debugAccountSummary(accountSummaryData);

    // PRIORITÉ 1 : Chercher TOUS les champs DailyPnL possibles dans accountSummary
    // IBKR peut fournir DailyPnL avec différents suffixes (devise, type, etc.)
    // Liste exhaustive de tous les champs possibles (par ordre de priorité)
    const dailyPnLKeys = [
      'dailyPnL',           // camelCase standard
      'DailyPnL',           // PascalCase
      'DailyPnL$EUR',       // Par devise EUR
      'DailyPnL-EUR',       // Avec tiret
      'DailyPnL$USD',       // Par devise USD
      'DailyPnL-USD',       
      'DailyPnL-S',         // Securities
      'DailyPnL-C',         // Commodities
      'DayPnL',             // Variante courte
      'TodayPnL',           // Variante
      'MTDPnL',             // Month-to-date (si début de mois)
      'DailyPnLRealized',   // Réalisé du jour
      'DailyPnLUnrealized'  // Non réalisé du jour (mais attention, c'est du jour, pas total)
    ];
    
    // Logs de débogage désactivés
    
    // Essayer chaque champ DailyPnL dans l'ordre de priorité
    for (const key of dailyPnLKeys) {
      const fieldValue = accountSummaryData?.[key];
      
      if (fieldValue !== undefined && fieldValue !== null) {
        // Si c'est un objet avec .value
        if (typeof fieldValue === 'object' && fieldValue.value !== undefined) {
          const dailyPnL = parseFloat(fieldValue.value);
          if (!isNaN(dailyPnL) && dailyPnL !== 0) {
            const currency = fieldValue.currency || accountSummaryData.currency || 'EUR';
            let finalValue = dailyPnL;
            
            // Si la devise n'est pas EUR, essayer de convertir
            if (currency !== 'EUR') {
              const exchangeRateKey = `ExchangeRate-${currency}`;
              const rate = accountSummaryData?.[exchangeRateKey]?.value || 
                          accountSummaryData?.[`exchangeRate${currency}`]?.value || 1.0;
              
              if (rate !== 1.0) {
                finalValue = dailyPnL * rate;
              } else {
                console.warn(`⚠️ [calculateDailyPnL] Taux de change non trouvé pour ${currency}, utilisation valeur brute`);
              }
            }
            
            return {
              value: finalValue,
              currency: 'EUR'
            };
          }
        } 
        // Si c'est une valeur directe
        else {
          const dailyPnL = parseFloat(fieldValue);
          if (!isNaN(dailyPnL) && dailyPnL !== 0) {
            return {
              value: dailyPnL,
              currency: accountSummaryData.currency || 'EUR'
            };
          }
        }
      }
    }
    
    // Si aucun champ DailyPnL non nul trouvé, chercher aussi dans les clés dynamiques
    const allKeys = Object.keys(accountSummaryData || {});
    const dynamicDailyPnLFields = allKeys.filter(key => 
      (key.toLowerCase().includes('dailypnl') || 
       key.toLowerCase().includes('daypnl') || 
       key.toLowerCase().includes('todaypnl')) &&
      !key.toLowerCase().includes('unrealized') && 
      !key.toLowerCase().includes('realized') &&
      !dailyPnLKeys.includes(key)
    );
    
    if (dynamicDailyPnLFields.length > 0) {
      for (const field of dynamicDailyPnLFields) {
        const fieldValue = accountSummaryData[field];
        if (fieldValue && typeof fieldValue === 'object' && fieldValue.value !== undefined) {
          const dailyPnL = parseFloat(fieldValue.value);
          if (!isNaN(dailyPnL) && dailyPnL !== 0) {
            return {
              value: dailyPnL,
              currency: fieldValue.currency || accountSummaryData.currency || 'EUR'
            };
          }
        }
      }
    }
    
    // Si DailyPnL = 0 ou non trouvé, on continue avec le calcul depuis NetLiquidation
    // ⚠️ IMPORTANT : Ne PAS utiliser UnrealizedPnL + RealizedPnL comme fallback
    // UnrealizedPnL = P&L TOTAL depuis l'achat (pas du jour !)
    // RealizedPnL = P&L réalisé du jour (mais pas le total du jour)
    // DailyPnL = variation NetLiquidation depuis hier (ce qu'on veut)

    // PRIORITÉ 2 : Calculer depuis NetLiquidation actuel - RÉFÉRENCE TEMPORELLE
    // Essayer plusieurs références pour trouver celle qui donne le bon delta (-22k€)
    try {
      // Récupérer la valeur nette actuelle
      if (!currentNetLiquidation?.value) {
        console.warn('⚠️ [calculateDailyPnL] Valeur nette actuelle non disponible');
        console.groupEnd();
        return undefined;
      }
      
      const currentValue = parseFloat(currentNetLiquidation.value);
      if (isNaN(currentValue) || currentValue === 0) {
        console.warn('⚠️ [calculateDailyPnL] Valeur nette actuelle invalide:', currentValue);
        console.groupEnd();
        return undefined;
      }
      
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      
      // Liste de TOUTES les références possibles à essayer (par ordre de priorité)
      // NOTE: La clé ibkr_net_liquidation_close_${today} contient l'OUVERTURE du jour (première valeur sauvegardée)
      const possibleRefs = [
        // 1. Ouverture du jour (sauvegardée dans ibkr_net_liquidation_close_${today} comme première valeur)
        { 
          key: `ibkr_net_liquidation_close_${today}`, 
          label: 'Ouverture du jour (sauvegardée)',
          fallbackKey: `ibkr_net_liquidation_open_${today}`
        },
        // 2. Ouverture du marché aujourd'hui (première valeur du jour)
        { 
          key: `ibkr_net_liquidation_open_${today}`, 
          label: 'Ouverture du jour',
          fallbackKey: 'ibkr_net_liquidation_market_open'
        },
        // 3. Clôture d'hier (standard)
        { 
          key: 'ibkr_net_liquidation_close_previous', 
          label: 'Clôture hier',
          fallbackKey: null
        },
        // 4. Clôture d'hier par date
        { 
          key: `ibkr_net_liquidation_close_${yesterdayStr}`, 
          label: 'Clôture hier (date)',
          fallbackKey: null
        },
        // 5. Ouverture du marché (heure fixe)
        { 
          key: 'ibkr_net_liquidation_market_open', 
          label: 'Ouverture marché',
          fallbackKey: null
        }
      ];
      
      let bestMatch = null;
      let bestDelta = null;
      let bestKey = null;
      
      // Essayer chaque référence
      for (const ref of possibleRefs) {
        let refValue = localStorage.getItem(ref.key);
        
        // Essayer le fallback si disponible
        if (!refValue && ref.fallbackKey) {
          refValue = localStorage.getItem(ref.fallbackKey);
        }
        
        if (refValue) {
          const parsed = parseFloat(refValue);
          if (!isNaN(parsed) && parsed > 0) {
            const delta = currentValue - parsed;
            
            // Si le delta est proche de -22k€ (±1000€ de tolérance), c'est probablement la bonne référence
            if (Math.abs(delta - (-22000)) < 1000) {
              if (!bestMatch || Math.abs(delta - (-22000)) < Math.abs(bestDelta - (-22000))) {
                bestMatch = parsed;
                bestDelta = delta;
                bestKey = ref.key;
              }
            }
          }
        }
      }
      
      // Si on a trouvé une bonne correspondance, l'utiliser
      if (bestMatch && bestDelta !== null) {
        return {
          value: bestDelta,
          currency: currentNetLiquidation.currency || 'EUR'
        };
      }
      
      // Sinon, utiliser la référence la plus récente disponible
      // On priorise les références dans cet ordre :
      // 1. Ouverture du jour (sauvegardée dans ibkr_net_liquidation_close_${today})
      // 2. Clôture d'hier par date
      // 3. Clôture d'hier (previous)
      // 4. Ouverture marché
      const priorityOrder = [
        `ibkr_net_liquidation_close_${today}`, // Ouverture du jour (première valeur sauvegardée aujourd'hui)
        `ibkr_net_liquidation_open_${today}`, // Ouverture du jour (alternative)
        `ibkr_net_liquidation_close_${yesterdayStr}`, // Clôture hier par date
        'ibkr_net_liquidation_close_previous', // Clôture hier (previous)
        'ibkr_net_liquidation_market_open' // Ouverture marché
      ];
      
      for (const key of priorityOrder) {
        const ref = possibleRefs.find(r => r.key === key);
        if (!ref) continue;
        
        let refValue = localStorage.getItem(ref.key);
        if (!refValue && ref.fallbackKey) {
          refValue = localStorage.getItem(ref.fallbackKey);
        }
        
        if (refValue) {
          const parsed = parseFloat(refValue);
          if (!isNaN(parsed) && parsed > 0) {
            const calculatedPnL = currentValue - parsed;
            
            return {
              value: calculatedPnL,
              currency: currentNetLiquidation.currency || 'EUR'
            };
          }
        }
      }
      
      // Aucune référence temporelle disponible
    } catch (error) {
      console.warn('⚠️ [calculateDailyPnL] Erreur lors du calcul:', error);
    }

    // PRIORITÉ 3 : Récupérer la dernière valeur calculée depuis localStorage (seulement si récente < 24h)
    try {
      const lastPnL = localStorage.getItem('ibkr_last_daily_pnl');
      const lastTimestamp = localStorage.getItem('ibkr_last_daily_pnl_timestamp');
      
      if (lastPnL && lastTimestamp) {
        const hoursSinceUpdate = (Date.now() - parseInt(lastTimestamp)) / (1000 * 60 * 60);
        const pnlValue = parseFloat(lastPnL);
        
        // Utiliser seulement si la valeur est récente (< 24h) et non nulle
        if (!isNaN(pnlValue) && pnlValue !== 0 && hoursSinceUpdate < 24) {
          return {
            value: pnlValue,
            currency: localStorage.getItem('ibkr_last_daily_pnl_currency') || currentNetLiquidation?.currency || 'EUR'
          };
        } else if (hoursSinceUpdate >= 24) {
          console.warn(`⚠️ [calculateDailyPnL] Dernière valeur trop ancienne (${hoursSinceUpdate.toFixed(1)}h), ignorée`);
        }
      } else if (lastPnL) {
        // Pas de timestamp, utiliser quand même si la valeur existe (compatibilité)
        const pnlValue = parseFloat(lastPnL);
        if (!isNaN(pnlValue) && pnlValue !== 0) {
          return {
            value: pnlValue,
            currency: localStorage.getItem('ibkr_last_daily_pnl_currency') || currentNetLiquidation?.currency || 'EUR'
          };
        }
      }
    } catch (error) {
      console.warn('⚠️ [calculateDailyPnL] Erreur lors de la récupération de la dernière valeur:', error);
    }

    // PRIORITÉ 4 : Si aucune méthode ne fonctionne, retourner undefined
    console.warn('❌ [calculateDailyPnL] Impossible de calculer le P&L journalier - aucune méthode disponible');
    return undefined;
  }, [isConnected]);

  // Fonction pour sauvegarder la valeur de clôture (à appeler à la clôture du marché)
  // Fonction pour sauvegarder la valeur à l'ouverture du marché
  const saveMarketOpenValue = useCallback((netLiquidation) => {
    if (!netLiquidation?.value) return;
    
    try {
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const hour = now.getHours();
      const minute = now.getMinutes();
      
      // Sauvegarder UNIQUEMENT entre 9h25 et 9h35 (ouverture marché US)
      // Ou entre 8h25 et 8h35 (ouverture marché Europe)
      const isMarketOpenTime = 
        (hour === 9 && minute >= 25 && minute <= 35) ||  // US: 9h30
        (hour === 8 && minute >= 25 && minute <= 35) ||  // Europe: 8h30
        (hour === 15 && minute >= 25 && minute <= 35);   // Alternative: 15h30
      
      if (isMarketOpenTime) {
        const openKey = `ibkr_net_liquidation_open_${today}`;
        const marketOpenKey = 'ibkr_net_liquidation_market_open';
        const existing = localStorage.getItem(openKey);
        
        if (!existing) {
          const netLiq = parseFloat(netLiquidation.value);
          localStorage.setItem(openKey, netLiq.toString());
          localStorage.setItem(marketOpenKey, netLiq.toString());
          localStorage.setItem('ibkr_market_open_date', today);
        }
      }
    } catch (error) {
      console.warn('⚠️ [saveMarketOpenValue] Erreur lors de la sauvegarde:', error);
    }
  }, []);

  const saveClosingValue = useCallback((netLiquidation) => {
    if (!netLiquidation?.value) return;
    
    try {
      const today = new Date().toISOString().split('T')[0];
      const closingKey = `ibkr_net_liquidation_close_${today}`;
      const previousClosingKey = `ibkr_net_liquidation_close_previous`;
      
      // Sauvegarder la valeur actuelle comme clôture d'aujourd'hui
      localStorage.setItem(closingKey, netLiquidation.value.toString());
      
      // Si c'est un nouveau jour, mettre à jour la valeur précédente
      const lastSavedDate = localStorage.getItem('ibkr_last_closing_date');
      if (lastSavedDate !== today) {
        // Récupérer la clôture d'hier si elle existe
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];
        const yesterdayKey = `ibkr_net_liquidation_close_${yesterdayStr}`;
        const yesterdayValue = localStorage.getItem(yesterdayKey);
        
        if (yesterdayValue) {
          localStorage.setItem(previousClosingKey, yesterdayValue);
        } else {
          // Fallback: utiliser la clôture d'aujourd'hui comme référence
          localStorage.setItem(previousClosingKey, netLiquidation.value.toString());
        }
        
        localStorage.setItem('ibkr_last_closing_date', today);
      }
      
      // Toujours essayer de sauvegarder l'ouverture du marché si c'est le bon moment
      saveMarketOpenValue(netLiquidation);
    } catch (error) {
      console.warn('⚠️ [saveClosingValue] Erreur lors de la sauvegarde:', error);
    }
  }, [saveMarketOpenValue]);

  // Effet pour mettre à jour le P&L journalier de manière persistante
  useEffect(() => {
    if (!account || !accountSummary) return;
    
    const calculatedResult = calculateDailyPnL(account.net_liquidation, accountSummary);
    
    if (calculatedResult && calculatedResult.value !== undefined && calculatedResult.value !== null) {
      const calculatedValue = calculatedResult.value;
      const previousValue = lastValidDailyPnLRef.current;
      
      // Si le calcul retourne 0 ET qu'on a déjà une valeur valide non nulle, on garde l'ancienne
      if (calculatedValue === 0 && previousValue && previousValue.value !== 0 && previousValue.value !== null) {
        // Ne pas mettre à jour, garder la valeur précédente
        return;
      }
      
      // Vérifier si la valeur a vraiment changé pour éviter les mises à jour inutiles
      if (previousValue && previousValue.value === calculatedValue) {
        // La valeur n'a pas changé, ne pas mettre à jour
        return;
      }
      
      // Nouvelle valeur valide (non nulle) ou première valeur
      setLastValidDailyPnL(calculatedResult);
      lastValidDailyPnLRef.current = calculatedResult;
      
      // Sauvegarder dans localStorage pour persistence
      try {
        localStorage.setItem('ibkr_last_daily_pnl', calculatedValue.toString());
        localStorage.setItem('ibkr_last_daily_pnl_currency', calculatedResult.currency || 'EUR');
        localStorage.setItem('ibkr_last_daily_pnl_timestamp', Date.now().toString());
      } catch (error) {
        console.warn('⚠️ Erreur lors de la sauvegarde du P&L:', error);
      }
    }
  }, [account, accountSummary, calculateDailyPnL]);

  // Fonction pour charger le résumé du compte (définie avant les useEffect qui l'utilisent)
  // DOIT être définie après debugAccountSummary et calculateDailyPnL
  const loadAccountSummaryData = useCallback(async () => {
    const t0 = performance.now();
    setLoadingAccountSummary(true);
    try {
      const response = await twsAPI.getAccountSummary();
      setAccountSummary(response.data);
      
      // Debug complet AVANT traitement (désactivé)
      debugAccountSummary(response.data);
      
      // Afficher toutes les métriques importantes avec explications
      // Logs de débogage désactivés
      
      // Afficher la comparaison pour clarifier
      const previousClose = localStorage.getItem('ibkr_net_liquidation_close_previous');
      if (previousClose && response.data?.netLiquidation?.value) {
        const currentNetLiq = parseFloat(response.data.netLiquidation.value);
        const closeValue = parseFloat(previousClose);
        const calculatedDailyPnL = currentNetLiq - closeValue;
        
        // Logs de débogage désactivés
        // Logs de débogage désactivés
        console.groupEnd();
      }
      
      // Logs de débogage désactivés
      
      console.groupEnd();
      
      // Sauvegarder la valeur de clôture pour le calcul du P&L journalier
      // IMPORTANT : La clé d'aujourd'hui contient l'OUVERTURE du jour (première valeur)
      // La clé "previous" contient la CLÔTURE d'hier (pour calculer le P&L du jour)
      if (response.data?.netLiquidation?.value) {
        try {
          const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
          const todayKey = `ibkr_net_liquidation_close_${today}`;
          const previousClosingKey = `ibkr_net_liquidation_close_previous`;
          const lastSaveDateKey = 'ibkr_last_save_date';
          
          const currentValue = parseFloat(response.data.netLiquidation.value);
          if (isNaN(currentValue) || currentValue <= 0) {
            console.warn('⚠️ [saveClosingValue] Valeur nette invalide:', currentValue);
            return;
          }
          
          // Récupérer la dernière date de sauvegarde
          const lastSavedDate = localStorage.getItem(lastSaveDateKey);
          
          // Si c'est un nouveau jour, sauvegarder la clôture d'hier
          if (lastSavedDate && lastSavedDate !== today) {
            // Récupérer la valeur d'hier (qui était la valeur actuelle à la fin d'hier)
            const yesterdayKey = `ibkr_net_liquidation_close_${lastSavedDate}`;
            const yesterdayValue = localStorage.getItem(yesterdayKey);
            
            if (yesterdayValue) {
              const parsed = parseFloat(yesterdayValue);
              if (!isNaN(parsed) && parsed > 0) {
                // Sauvegarder la valeur d'hier comme clôture d'hier (pour calculer le P&L du jour)
                localStorage.setItem(previousClosingKey, yesterdayValue);
              }
            } else {
              // Si pas de valeur d'hier, utiliser la valeur actuelle comme référence
              // (cas où on n'a pas de données d'hier)
              console.warn('⚠️ [saveClosingValue] Pas de valeur d\'hier trouvée, utilisation valeur actuelle comme référence');
              localStorage.setItem(previousClosingKey, currentValue.toString());
            }
            
            // Sauvegarder la valeur actuelle comme ouverture d'aujourd'hui
            // ⚠️ NE PAS écraser si on a déjà une valeur pour aujourd'hui (garder l'ouverture)
            const existingToday = localStorage.getItem(todayKey);
            if (!existingToday) {
              localStorage.setItem(todayKey, currentValue.toString());
            }
            
            localStorage.setItem(lastSaveDateKey, today);
          } else if (!lastSavedDate) {
            // Première sauvegarde jamais faite
            localStorage.setItem(todayKey, currentValue.toString());
            localStorage.setItem(lastSaveDateKey, today);
          } else {
            // Même jour : mettre à jour seulement si c'est la première valeur du jour (ouverture)
            // Sinon, on garde l'ouverture et on ne met pas à jour
            const todaySaved = localStorage.getItem(todayKey);
            if (!todaySaved) {
              // Première valeur du jour = ouverture
              localStorage.setItem(todayKey, currentValue.toString());
            }
            // Sinon on garde l'ouverture déjà sauvegardée pour référence
          }
        } catch (error) {
          console.warn('⚠️ [saveClosingValue] Erreur lors de la sauvegarde:', error);
        }
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement du résumé du compte:', error);
      if (error.response?.status === 503) {
        console.warn('⚠️ TWS non connecté');
      }
    } finally {
      setLoadingAccountSummary(false);
      const duration = performance.now() - t0;
      logDataLoad('Portefeuille - Résumé compte', duration, 'positions + P&L');
    }
  }, [isConnected, account, debugAccountSummary, calculateDailyPnL, showToast]);

  // Charger le résumé du compte quand connecté (loadOrders différé à l'onglet Ordres)
  useEffect(() => {
    if (isConnected) {
      loadAccountSummaryData();
    }
  }, [isConnected, loadAccountSummaryData]);

  // Fonction pour charger les catégories et les données d'enrichissement
  const loadCategoriesAndEnrichment = useCallback(async () => {
    if (positions.length === 0) return;
    
    const t0 = performance.now();
    setLoadingCategories(true);
    try {
      // Fonction helper pour charger depuis le cache (même expiré si allowExpired = true)
      const loadCategoriesFromCache = (allowExpired = false) => {
        try {
          const savedCats = sessionStorage.getItem('portefeuille_categories');
          const savedCatsTimestamp = sessionStorage.getItem('portefeuille_categories_timestamp');
          if (savedCats && savedCatsTimestamp) {
            const age = Date.now() - parseInt(savedCatsTimestamp, 10);
            if (allowExpired || age < 5 * 60 * 1000) { // Moins de 5 minutes ou autoriser expiré
              const cats = JSON.parse(savedCats);
              const activeCats = cats.filter(cat => cat.is_active);
              if (activeCats.length > 0) {
                setCategories(activeCats);
                return activeCats;
              }
            }
          }
        } catch (e) {
          // Ignorer les erreurs de sessionStorage
        }
        return [];
      };
      
      // Vérifier d'abord le cache sessionStorage
      let activeCategories = loadCategoriesFromCache(false);
      
      // Charger les catégories depuis l'API si le cache n'est pas disponible ou expiré
      if (activeCategories.length === 0) {
        try {
          // Utiliser un timeout réduit de 10 secondes (comme pour ActifDetail)
          // Utiliser api.get() directement pour avoir un contrôle sur le timeout
          const categoriesResponse = await api.get('/categories', { timeout: 10000 });
          activeCategories = categoriesResponse.data.filter(cat => cat.is_active);
          setCategories(activeCategories);
          
          // Mettre en cache dans sessionStorage
          try {
            sessionStorage.setItem('portefeuille_categories', JSON.stringify(categoriesResponse.data));
            sessionStorage.setItem('portefeuille_categories_timestamp', Date.now().toString());
          } catch (e) {
            // Ignorer les erreurs de sessionStorage
          }
        } catch (err) {
          console.error('Error loading categories:', err);
          
          // En cas d'erreur (timeout, réseau, etc.), essayer d'utiliser le cache même expiré
          const cachedCategories = loadCategoriesFromCache(true);
          if (cachedCategories.length > 0) {
            activeCategories = cachedCategories;
          } else {
            console.warn('⚠️ [Portefeuille] Impossible de charger les catégories et aucun cache disponible');
          }
        }
      }
      
      // S'assurer qu'on a des catégories avant de continuer
      if (activeCategories.length === 0) {
        console.warn('⚠️ [Portefeuille] Aucune catégorie disponible, impossible de charger l\'enrichissement');
        setLoadingCategories(false);
        return;
      }
      
      // Créer un map catégorie_id -> nom
      const categoryMap = new Map();
      activeCategories.forEach(cat => {
        categoryMap.set(cat.id, cat.name);
      });
      
      // Charger les données d'enrichissement pour toutes les positions en une seule requête
      const enrichmentMapLocal = new Map();
      const symbols = positions.map(p => p.symbol).filter(Boolean);
      
      if (symbols.length > 0) {
        try {
          // Utiliser le nouvel endpoint bulk pour charger toutes les données en une seule requête
          const bulkResponse = await assetEnrichmentAPI.getBulkBySymbols(symbols);
          
          if (bulkResponse.data && Array.isArray(bulkResponse.data)) {
            bulkResponse.data.forEach((item) => {
              if (item.categorie_name) {
                enrichmentMapLocal.set(item.symbol, item.categorie_name);
              }
            });
          }
        } catch (err) {
          console.warn('⚠️ [Portefeuille] Erreur bulk enrichissement, fallback individuel:', err.message);
          // En cas d'erreur avec le bulk, fallback sur les appels individuels
          // Charger les données d'enrichissement en parallèle par chunks pour éviter la surcharge
          const chunkSize = 10;
          for (let i = 0; i < symbols.length; i += chunkSize) {
            const chunk = symbols.slice(i, i + chunkSize);
            const enrichmentPromises = chunk.map(async (symbol) => {
              try {
                const enrichmentResponse = await assetEnrichmentAPI.getBySymbol(symbol);
                if (enrichmentResponse.data && enrichmentResponse.data.categorie_id) {
                  const categoryName = categoryMap.get(enrichmentResponse.data.categorie_id);
                  if (categoryName) {
                    enrichmentMapLocal.set(symbol, categoryName);
                  }
                }
              } catch (err) {
                // 404 est normal si aucune donnée d'enrichissement n'existe
                // Ignorer silencieusement
              }
            });
            
            await Promise.all(enrichmentPromises);
          }
        }
      }
      setEnrichmentMap(enrichmentMapLocal);
    } catch (err) {
      console.error('Error loading categories:', err);
    } finally {
      const duration = performance.now() - t0;
      setLoadingCategories(false);
      logDataLoad('Portefeuille - Catégories', duration, 'catégories + enrichissement');
    }
  }, [positions]);

  // Charger les catégories au montage du composant (même sans positions)
  useEffect(() => {
    // Charger les catégories depuis le cache si disponible
    const loadCategoriesFromCache = () => {
      try {
        const savedCats = sessionStorage.getItem('portefeuille_categories');
        const savedCatsTimestamp = sessionStorage.getItem('portefeuille_categories_timestamp');
        if (savedCats && savedCatsTimestamp) {
          const age = Date.now() - parseInt(savedCatsTimestamp, 10);
          if (age < 5 * 60 * 1000) { // Moins de 5 minutes
            const cats = JSON.parse(savedCats);
            const activeCats = cats.filter(cat => cat.is_active);
            if (activeCats.length > 0 && categories.length === 0) {
              setCategories(activeCats);
            }
          }
        }
      } catch (e) {
        // Ignorer les erreurs de sessionStorage
      }
    };
    
    // Charger depuis le cache au montage
    loadCategoriesFromCache();
  }, []); // Seulement au montage
  
  // Charger les catégories et les données d'enrichissement quand les positions changent
  useEffect(() => {
    if (positions.length > 0) {
      loadCategoriesAndEnrichment();
    } else if (categories.length === 0) {
      // Si pas de positions mais pas de catégories non plus, charger les catégories quand même
      // (pour qu'elles soient disponibles si des positions arrivent plus tard)
      const loadCategoriesOnly = async () => {
        try {
          // Vérifier le cache d'abord
          const savedCats = sessionStorage.getItem('portefeuille_categories');
          const savedCatsTimestamp = sessionStorage.getItem('portefeuille_categories_timestamp');
          if (savedCats && savedCatsTimestamp) {
            const age = Date.now() - parseInt(savedCatsTimestamp, 10);
            if (age < 5 * 60 * 1000) {
              const cats = JSON.parse(savedCats);
              const activeCats = cats.filter(cat => cat.is_active);
              if (activeCats.length > 0) {
                setCategories(activeCats);
                return;
              }
            }
          }
          
          // Si pas de cache valide, charger depuis l'API
          setLoadingCategories(true);
          try {
            const categoriesResponse = await api.get('/categories', { timeout: 10000 });
            const activeCats = categoriesResponse.data.filter(cat => cat.is_active);
            setCategories(activeCats);
            
            // Mettre en cache
            try {
              sessionStorage.setItem('portefeuille_categories', JSON.stringify(categoriesResponse.data));
              sessionStorage.setItem('portefeuille_categories_timestamp', Date.now().toString());
            } catch (e) {
              // Ignorer les erreurs
            }
          } catch (err) {
            console.error('Error loading categories:', err);
            // En cas d'erreur, utiliser le cache même expiré
            if (savedCats) {
              try {
                const cats = JSON.parse(savedCats);
                const activeCats = cats.filter(cat => cat.is_active);
                if (activeCats.length > 0) {
                  setCategories(activeCats);
                }
              } catch (e) {
                // Ignorer
              }
            }
          } finally {
            setLoadingCategories(false);
          }
        } catch (err) {
          console.error('Error in loadCategoriesOnly:', err);
          setLoadingCategories(false);
        }
      };
      
      loadCategoriesOnly();
    }
  }, [positions, loadCategoriesAndEnrichment, categories.length]);

  // Écouter les événements de mise à jour d'enrichissement depuis ActifDetail
  useEffect(() => {
    const handleEnrichmentUpdated = async (event) => {
      // Recharger les données d'enrichissement quand un enrichissement est sauvegardé
      const symbol = event.detail?.symbol;
      
      if (symbol && positions.length > 0) {
        try {
          // Utiliser le cache pour les catégories, recharger depuis l'API seulement si nécessaire
          let activeCategories = categories;
          if (activeCategories.length === 0) {
            // Vérifier le cache sessionStorage
            try {
              const savedCats = sessionStorage.getItem('portefeuille_categories');
              const savedCatsTimestamp = sessionStorage.getItem('portefeuille_categories_timestamp');
              if (savedCats && savedCatsTimestamp) {
                const age = Date.now() - parseInt(savedCatsTimestamp, 10);
                if (age < 5 * 60 * 1000) { // Moins de 5 minutes
                  const cats = JSON.parse(savedCats);
                  activeCategories = cats.filter(cat => cat.is_active);
                  setCategories(activeCategories);
                }
              }
            } catch (e) {
              // Ignorer les erreurs de sessionStorage
            }
            
            // Si toujours vide, charger depuis l'API
            if (activeCategories.length === 0) {
              try {
                // Utiliser un timeout réduit de 10 secondes
                const categoriesResponse = await api.get('/categories', { timeout: 10000 });
                activeCategories = categoriesResponse.data.filter(cat => cat.is_active);
                setCategories(activeCategories);
                
                // Mettre en cache
                try {
                  sessionStorage.setItem('portefeuille_categories', JSON.stringify(categoriesResponse.data));
                  sessionStorage.setItem('portefeuille_categories_timestamp', Date.now().toString());
                } catch (e) {
                  // Ignorer les erreurs de sessionStorage
                }
              } catch (err) {
                console.error('Error loading categories in enrichment update:', err);
                // En cas d'erreur, utiliser le cache même expiré
                try {
                  const savedCats = sessionStorage.getItem('portefeuille_categories');
                  if (savedCats) {
                    const cats = JSON.parse(savedCats);
                    activeCategories = cats.filter(cat => cat.is_active);
                    setCategories(activeCategories);
                  }
                } catch (e) {
                  // Ignorer les erreurs
                }
              }
            }
          }
          
          // Créer un map catégorie_id -> nom
          const categoryMap = new Map();
          activeCategories.forEach(cat => {
            categoryMap.set(cat.id, cat.name);
          });
          
          // Recharger uniquement les données d'enrichissement pour le symbole mis à jour
          try {
            const enrichmentResponse = await assetEnrichmentAPI.getBySymbol(symbol);
            if (enrichmentResponse.data && enrichmentResponse.data.categorie_id) {
              const categoryName = categoryMap.get(enrichmentResponse.data.categorie_id);
              if (categoryName) {
                // Mettre à jour uniquement ce symbole dans l'enrichmentMap
                setEnrichmentMap(prev => {
                  const newMap = new Map(prev);
                  newMap.set(symbol, categoryName);
                  return newMap;
                });
              } else {
                // Si pas de catégorie, retirer du map
                setEnrichmentMap(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(symbol);
                  return newMap;
                });
              }
            } else {
              // Si pas de catégorie_id, retirer du map
              setEnrichmentMap(prev => {
                const newMap = new Map(prev);
                newMap.delete(symbol);
                return newMap;
              });
            }
          } catch (err) {
            // 404 signifie qu'il n'y a plus de données d'enrichissement, retirer du map
            if (err.response?.status === 404) {
              setEnrichmentMap(prev => {
                const newMap = new Map(prev);
                newMap.delete(symbol);
                return newMap;
              });
            } else {
              console.error(`Error loading enrichment for ${symbol}:`, err);
            }
          }
        } catch (err) {
          console.error('Error reloading categories:', err);
          // En cas d'erreur, recharger toutes les données d'enrichissement
          if (positions.length > 0) {
            loadCategoriesAndEnrichment();
          }
        }
      } else if (positions.length > 0) {
        // Si pas de symbole spécifique, recharger toutes les données
        loadCategoriesAndEnrichment();
      }
    };

    window.addEventListener('enrichmentUpdated', handleEnrichmentUpdated);
    return () => {
      window.removeEventListener('enrichmentUpdated', handleEnrichmentUpdated);
    };
  }, [positions, loadCategoriesAndEnrichment]);

  useEffect(() => {
    if (activeTab === 0) loadValuationEvolutionPortefeuille();
  }, [activeTab, loadValuationEvolutionPortefeuille]);

  // Forcer le re-render du graphique évolution quand l'onglet Synthèse (0) devient actif — même logique que Compte
  useEffect(() => {
    if (activeTab === 0) {
      const timer = setTimeout(() => {
        setIsEvolutionChartVisible(true);
        setEvolutionChartKey(prev => prev + 1);
      }, 150);
      return () => {
        clearTimeout(timer);
        setIsEvolutionChartVisible(false);
      };
    } else {
      setIsEvolutionChartVisible(false);
    }
  }, [activeTab]);

  // Mesurer le conteneur du graphique évolution (évite ResponsiveContainer avec dimensions 0)
  useEffect(() => {
    if (!evolutionChartContainerRef.current || !isEvolutionChartVisible) {
      if (!isEvolutionChartVisible) setEvolutionChartContainerDims({ width: 0, height: 0 });
      return;
    }
    const checkDims = () => {
      const rect = evolutionChartContainerRef.current?.getBoundingClientRect();
      if (rect && rect.width > 0 && rect.height > 0) {
        setEvolutionChartContainerDims({ width: rect.width, height: rect.height });
      }
    };
    let timeoutId = null;
    // Double rAF + micro délai pour laisser le layout de l'onglet (évite 0x0 en flex)
    let rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        timeoutId = setTimeout(checkDims, 50);
      });
    });
    const resizeObserver = new ResizeObserver(() => {
      if (evolutionResizeTimeoutRef.current) clearTimeout(evolutionResizeTimeoutRef.current);
      evolutionResizeTimeoutRef.current = setTimeout(() => {
        const rect = evolutionChartContainerRef.current?.getBoundingClientRect();
        if (rect && rect.width > 0 && rect.height > 0) {
          const newDims = { width: rect.width, height: rect.height };
          setEvolutionChartContainerDims(newDims);
          const widthDiff = Math.abs(newDims.width - lastEvolutionChartDimsRef.current.width);
          const heightDiff = Math.abs(newDims.height - lastEvolutionChartDimsRef.current.height);
          if (widthDiff > 5 || heightDiff > 5) {
            lastEvolutionChartDimsRef.current = newDims;
            setEvolutionChartKey(prev => prev + 1);
          }
        }
      }, 500);
    });
    resizeObserver.observe(evolutionChartContainerRef.current);
    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId != null) clearTimeout(timeoutId);
      if (evolutionResizeTimeoutRef.current) clearTimeout(evolutionResizeTimeoutRef.current);
      resizeObserver.disconnect();
    };
  }, [isEvolutionChartVisible]);

  // Données pour le graphique "Evolution de la valeur du portefeuille" (valeur uniquement, pas ATH ni apports)
  const chartDataEvolutionPortefeuille = useMemo(() => {
    if (!valuationEvolutionPortefeuille?.data?.length) return [];
    return valuationEvolutionPortefeuille.data.map((item) => ({
      date: item.date,
      valeur: item.valeur_totale
    }));
  }, [valuationEvolutionPortefeuille]);

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'EUR': '€',
      'USD': '$',
      'GBP': '£',
      'JPY': '¥',
      'CHF': 'CHF',
      'CAD': 'C$',
      'AUD': 'A$',
    };
    return symbols[currency] || currency;
  };

  const formatCurrency = (value, currency = 'EUR', decimals = 2) => {
    if (value === null || value === undefined) return '-';
    const formatted = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
    const symbol = getCurrencySymbol(currency);
    return `${formatted} ${symbol}`;
  };

  const calculateLeverage = () => {
    const acc = accountForMetrics ?? displayAccount ?? account;
    if (!acc || !acc.gross_position_value?.value || !acc.net_liquidation?.value) {
      return '-';
    }
    const leverage = acc.gross_position_value.value / acc.net_liquidation.value;
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(leverage) + 'x';
  };

  const getAvailableFunds = () => {
    // Récupérer le cash disponible depuis IBKR (AvailableFunds)
    if (!accountSummary || !accountSummary.availableFunds) {
      return '-';
    }
    const value = accountSummary.availableFunds.value;
    // Si valeur > 0 : diviser par 0.15, sinon afficher la valeur telle quelle
    const calculatedValue = value > 0 ? value / 0.15 : value;
    return formatCurrency(
      calculatedValue, 
      accountSummary.availableFunds.currency, 
      0
    );
  };

  // Config des métriques (évite la répétition des 6 cartes)
  const metricsConfig = [
    { label: 'Valeur Nette', getValue: (acc) => formatCurrency(acc?.net_liquidation?.value, acc?.net_liquidation?.currency, 0), help: 'Valeur totale du compte' },
    { label: 'Marge', getValue: (acc) => formatCurrency(acc?.total_cash?.value, acc?.total_cash?.currency, 0), help: 'Liquidités disponibles' },
    { label: 'Valeur de la Position', getValue: (acc) => formatCurrency(acc?.gross_position_value?.value, acc?.gross_position_value?.currency, 0), help: 'Valeur totale des positions ouvertes' },
    { label: 'Levier', getValue: () => calculateLeverage(), help: 'Ratio Valeur Positions / Valeur Nette' },
    { label: 'P&L Non Réalisé', getValue: (acc) => formatCurrency(acc?.unrealized_pnl?.value, acc?.unrealized_pnl?.currency, 0), help: 'Plus/moins-value latente' },
    { label: 'Disponible', getValue: () => getAvailableFunds(), help: 'Pouvoir d\'achat disponible' },
  ];

  // Calculer les données pour les graphiques de répartition avec cache
  const portfolioDistributionData = useMemo(() => {
    // Logs désactivés pour performance
    
    if (positions.length === 0 || categories.length === 0) {
      // Log désactivé pour performance
      return { byCategory: [], byFragility: [] };
    }

    // Créer un hash simple des positions et de l'enrichmentMap pour détecter les changements
    const positionsHash = positions.map(p => `${p.symbol}:${p.marketValue || 0}`).join('|');
    const enrichmentMapHash = Array.from(enrichmentMap.entries()).sort().join('|');
    
    // Vérifier le cache (recalculer seulement toutes les 10 secondes ou si les données ont changé)
    const now = Date.now();
    const cacheAge = now - distributionCacheRef.current.timestamp;
    const cacheValid = 
      distributionCacheRef.current.data !== null &&
      cacheAge < 10000 && // Cache valide pendant 10 secondes
      distributionCacheRef.current.positionsHash === positionsHash &&
      distributionCacheRef.current.enrichmentMapHash === enrichmentMapHash;
    
    if (cacheValid) {
      // Retourner une copie pour éviter les mutations
      return {
        byCategory: [...distributionCacheRef.current.data.byCategory],
        byFragility: [...distributionCacheRef.current.data.byFragility]
      };
    }

    // Créer un map catégorie_id -> catégorie pour accès rapide
    const categoryMap = new Map();
    categories.forEach(cat => {
      categoryMap.set(cat.id, cat);
    });

    // Créer un map symbole -> catégorie depuis enrichmentMap
    const symbolToCategory = new Map();
    enrichmentMap.forEach((categoryName, symbol) => {
      // Trouver la catégorie par nom
      const category = categories.find(cat => cat.name === categoryName);
      if (category) {
        symbolToCategory.set(symbol, category);
      }
    });

    // Calculer les valeurs par catégorie
    const categoryValues = new Map();
    const fragilityValues = new Map();
    
    // Utiliser la valeur totale du compte comme référence (déjà en EUR)
    // Cette valeur est la source de vérité car elle vient directement d'IBKR en EUR
    const accountTotalValue = account?.gross_position_value?.value || account?.net_liquidation?.value || null;
    
    // Étape 1 : Calculer la valeur totale brute (sans conversion) pour normalisation
    let totalRawValue = 0;
    positions.forEach(position => {
      const marketValue = position.marketValue || 0;
      if (marketValue > 0) {
        totalRawValue += marketValue;
      }
    });
    
    // Étape 2 : Calculer le ratio de normalisation si on a la valeur du compte
    // Ce ratio permet de convertir toutes les valeurs pour qu'elles correspondent à la valeur réelle du compte
    let normalizationRatio = 1.0;
    if (accountTotalValue && totalRawValue > 0) {
      normalizationRatio = accountTotalValue / totalRawValue;
    }

    // Étape 3 : Calculer les valeurs par catégorie avec normalisation
    // On utilise directement les marketValue et on applique le ratio de normalisation
    // qui prend en compte toutes les conversions de devise nécessaires
    let positionsWithValue = 0;
    let positionsWithCategory = 0;
    positions.forEach(position => {
      const marketValue = position.marketValue || 0;
      if (marketValue <= 0) return;
      positionsWithValue++;

      // Appliquer le ratio de normalisation qui compense toutes les conversions de devise
      const valueInEUR = marketValue * normalizationRatio;

      // Trouver la catégorie pour ce symbole
      const category = symbolToCategory.get(position.symbol);
      
      if (category) {
        positionsWithCategory++;
        // Par catégorie
        const currentValue = categoryValues.get(category.name) || 0;
        categoryValues.set(category.name, currentValue + valueInEUR);

        // Par fragilité
        const fragilityType = category.is_fragile ? 'Fragile' : 'Anti-Fragile';
        const currentFragilityValue = fragilityValues.get(fragilityType) || 0;
        fragilityValues.set(fragilityType, currentFragilityValue + valueInEUR);
      } else {
        // Si pas de catégorie, mettre dans "Non catégorisé"
        const currentValue = categoryValues.get('Non catégorisé') || 0;
        categoryValues.set('Non catégorisé', currentValue + valueInEUR);
        
        // Pour la fragilité, on ne compte pas les non catégorisés
      }
    });
    
    // Logs de débogage désactivés

    // Convertir en tableaux pour les graphiques
    const byCategory = Array.from(categoryValues.entries())
      .map(([name, value]) => {
        const category = categories.find(cat => cat.name === name);
        const roundedValue = Math.round(value * 100) / 100;
        // Vérifier que la valeur est valide
        if (isNaN(roundedValue) || roundedValue <= 0) {
          console.warn(`⚠️ [Portefeuille] Valeur invalide pour catégorie ${name}:`, roundedValue);
          return null;
        }
        return {
          name,
          value: roundedValue,
          color: category?.color || '#CCCCCC'
        };
      })
      .filter(item => item !== null) // Filtrer les valeurs invalides
      .sort((a, b) => b.value - a.value);

    const byFragility = Array.from(fragilityValues.entries())
      .map(([name, value]) => {
        const roundedValue = Math.round(value * 100) / 100;
        // Vérifier que la valeur est valide
        if (isNaN(roundedValue) || roundedValue <= 0) {
          console.warn(`⚠️ [Portefeuille] Valeur invalide pour fragilité ${name}:`, roundedValue);
          return null;
        }
        return {
          name,
          value: roundedValue,
          color: name === 'Fragile' ? '#FF6B6B' : '#4ECDC4' // Couleurs par défaut pour fragilité
        };
      })
      .filter(item => item !== null) // Filtrer les valeurs invalides
      .sort((a, b) => b.value - a.value);

    // Créer des copies profondes pour éviter les mutations
    const result = { 
      byCategory: byCategory.map(item => ({ ...item })),
      byFragility: byFragility.map(item => ({ ...item }))
    };
    
    // Log désactivé pour performance
    
    // Mettre à jour le cache avec des copies
    distributionCacheRef.current = {
      data: {
        byCategory: result.byCategory.map(item => ({ ...item })),
        byFragility: result.byFragility.map(item => ({ ...item }))
      },
      timestamp: Date.now(),
      positionsHash,
      enrichmentMapHash
    };
    
    return result;
  }, [positions, categories, enrichmentMap, account?.gross_position_value?.value, account?.net_liquidation?.value]);

  // Colonnes pour le tableau des ordres
  const ordersColumns = [
    {
      key: 'symbol',
      label: 'Ticker',
      align: 'left'
    },
    {
      key: 'name',
      label: 'Nom',
      align: 'left',
      render: (value, row) => {
        // Récupérer le nom depuis les positions
        const position = positions.find(p => p.symbol === row.symbol);
        return position?.name || row.symbol || '-';
      }
    },
    {
      key: 'action',
      label: 'Action',
      align: 'left',
      render: (value) => {
        const color = value === 'BUY' ? '#28a745' : '#dc3545';
        return <span style={{ color, fontWeight: 'bold' }}>{value}</span>;
      }
    },
    {
      key: 'totalQuantity',
      label: 'Quantité',
      align: 'right',
      render: (value) => new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    },
    {
      key: 'orderType',
      label: 'Type',
      align: 'left'
    },
    {
      key: 'lmtPrice',
      label: 'Prix Limite',
      align: 'right',
      render: (value, row) => {
        if (value) {
          return formatCurrency(value, row.currency || 'USD', 2);
        }
        return '-';
      }
    },
    {
      key: 'auxPrice',
      label: 'Prix Stop',
      align: 'right',
      render: (value, row) => {
        if (value) {
          return formatCurrency(value, row.currency || 'USD', 2);
        }
        return '-';
      }
    },
    {
      key: 'status',
      label: 'Statut',
      align: 'left',
      render: (value) => {
        const statusColors = {
          'PreSubmitted': '#ffc107',
          'Submitted': '#17a2b8',
          'Filled': '#28a745',
          'Cancelled': '#6c757d',
          'PendingCancel': '#ffc107',
          'PendingSubmit': '#ffc107',
        };
        const color = statusColors[value] || '#6c757d';
        return <span style={{ color }}>{value}</span>;
      }
    },
    {
      key: 'filled',
      label: 'Rempli',
      align: 'right',
      render: (value) => new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    },
    {
      key: 'remaining',
      label: 'Restant',
      align: 'right',
      render: (value) => new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    },
    {
      key: 'avgFillPrice',
      label: 'Prix Moyen',
      align: 'right',
      render: (value, row) => {
        if (value) {
          return formatCurrency(value, row.currency || 'USD', 2);
        }
        return '-';
      }
    },
    {
      key: 'timeInForce',
      label: 'Validité',
      align: 'left'
    },
    {
      key: 'submittedDate',
      label: 'Date Soumission',
      align: 'left',
      render: (value) => {
        if (value) {
          try {
            const date = new Date(value);
            return new Intl.DateTimeFormat('fr-FR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              timeZone: 'Europe/Paris'
            }).format(date);
          } catch (e) {
            return value;
          }
        }
        return '-';
      }
    },
    {
      key: 'lastUpdateDate',
      label: 'Dernière Mise à Jour',
      align: 'left',
      render: (value) => {
        if (value) {
          try {
            const date = new Date(value);
            return new Intl.DateTimeFormat('fr-FR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              timeZone: 'Europe/Paris'
            }).format(date);
          } catch (e) {
            return value;
          }
        }
        return '-';
      }
    }
  ];

  // Fonctions de gestion des catégories
  const positionsColumns = [
    {
      key: 'symbol',
      label: 'Ticker',
      align: 'left',
      className: 'col-symbol'
    },
    {
      key: 'name',
      label: 'Nom',
      align: 'left',
      className: 'col-name'
    },
    {
      key: 'categorie',
      label: 'Catégorie',
      align: 'left',
      className: 'col-categorie',
      render: (value, row) => {
        const categoryName = enrichmentMap.get(row.symbol);
        return categoryName || '-';
      }
    },
    {
      key: 'secType',
      label: 'Type',
      align: 'left',
      className: 'col-sectype'
    },
    {
      key: 'position',
      label: 'Quantité',
      align: 'right',
      className: 'col-position',
      render: (value) => new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    },
    {
      key: 'avgCost',
      label: 'Prix Moyen',
      align: 'right',
      className: 'col-avg-cost',
      render: (value, row) => formatCurrency(value, row.currency, 2)
    },
    {
      key: 'initialValue',
      label: 'Valeur Initiale',
      align: 'right',
      className: 'col-initial-value',
      render: (value, row) => {
        const initialValue = (row.avgCost || 0) * (row.position || 0);
        return formatCurrency(initialValue, row.currency, 2);
      }
    },
    {
      key: 'marketPrice',
      label: 'Prix Marché',
      align: 'right',
      className: 'col-market-price',
      render: (value, row) => formatCurrency(value, row.currency, 2)
    },
    {
      key: 'marketValue',
      label: 'Valeur Marché',
      align: 'right',
      className: 'col-market-value',
      render: (value, row) => formatCurrency(value, row.currency, 2)
    },
    {
      key: 'unrealizedPNL',
      label: 'P&L Non Réalisé',
      align: 'right',
      className: 'col-unrealized-pnl',
      render: (value, row) => {
        const formatted = formatCurrency(value, row.currency, 2);
        const color = value >= 0 ? '#28a745' : '#dc3545';
        return <span style={{ color }}>{formatted}</span>;
      }
    },
    {
      key: 'unrealizedPNLPercent',
      label: 'P&L %',
      align: 'right',
      className: 'col-pnl-percent',
      render: (value, row) => {
        const unrealizedPNL = row.unrealizedPNL || 0;
        const avgCost = row.avgCost || 0;
        const position = row.position || 0;
        const initialValue = avgCost * Math.abs(position);
        
        if (initialValue === 0) {
          return '-';
        }
        
        const percent = (unrealizedPNL / initialValue) * 100;
        const formatted = new Intl.NumberFormat('fr-FR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(percent);
        
        const color = percent >= 0 ? '#28a745' : '#dc3545';
        return <span style={{ color }}>{formatted}%</span>;
      }
    }
  ];

  // Composant pour le graphique en secteurs
  const PieChartComponent = ({ data, title }) => {
    const containerRef = useRef(null);
    const [chartKey, setChartKey] = useState(0);
    // Initialiser isVisible à true si on est déjà sur l'onglet Synthèse
    const [isVisible, setIsVisible] = useState(activeTab === 0);
    // État pour vérifier les dimensions du conteneur
    const [containerDims, setContainerDims] = useState({ width: 0, height: 0 });
    // Forcer le re-render du graphique quand l'onglet devient actif (optimisé)
    // Utiliser un ref pour éviter les re-renders multiples
    const hasRenderedRef = useRef(false);
    useEffect(() => {
      if (activeTab === 0) {
        // Ne re-render qu'une seule fois quand l'onglet devient actif
        if (!hasRenderedRef.current) {
          const timer = setTimeout(() => {
            setIsVisible(true);
            setChartKey(prev => prev + 1);
            hasRenderedRef.current = true;
          }, 50);
          return () => {
            clearTimeout(timer);
          };
        }
      } else {
        setIsVisible(false);
        hasRenderedRef.current = false; // Reset pour le prochain affichage
      }
    }, [activeTab]); // Retirer 'title' des dépendances car il ne change pas
    
    // Vérifier les dimensions du conteneur quand il devient visible (optimisé pour rapidité)
    // Utiliser un ref pour éviter les mises à jour multiples
    const lastDimsRef = useRef({ width: 0, height: 0 });
    useEffect(() => {
      if (containerRef.current && isVisible) {
        const checkDimensions = () => {
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect && rect.width > 0 && rect.height > 0) {
            // Ne mettre à jour que si les dimensions ont vraiment changé
            const newDims = { width: rect.width, height: rect.height };
            if (lastDimsRef.current.width !== newDims.width || lastDimsRef.current.height !== newDims.height) {
              setContainerDims(newDims);
              lastDimsRef.current = newDims;
            }
            return true; // Dimensions trouvées
          }
          return false; // Dimensions non trouvées
        };
        
        // Vérifier immédiatement
        if (!checkDimensions()) {
          // Si pas de dimensions, retry rapide après 50ms
          const timer1 = setTimeout(() => {
            if (!checkDimensions()) {
              // Dernier retry après 100ms
              const timer2 = setTimeout(checkDimensions, 100);
              return () => clearTimeout(timer2);
            }
          }, 50);
          return () => clearTimeout(timer1);
        }
      } else {
        setContainerDims({ width: 0, height: 0 });
        lastDimsRef.current = { width: 0, height: 0 };
      }
    }, [isVisible]); // Retirer 'title' des dépendances
    
    // Observer les changements de taille du conteneur (une seule fois au montage)
    // Utiliser un ref pour éviter les re-rendus en boucle
    const resizeObserverRef = useRef(null);
    const timeoutRef = useRef(null);
    const lastResizeDimsRef = useRef({ width: 0, height: 0 }); // Déplacer useRef en dehors du useEffect
    
    useEffect(() => {
      if (!containerRef.current || !isVisible) {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
        return;
      }
      
      // Nettoyer l'ancien observer s'il existe
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      
      // Créer un nouvel observer avec debounce agressif
      resizeObserverRef.current = new ResizeObserver(() => {
        // Debounce plus agressif pour éviter les appels trop fréquents
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect && rect.width > 0 && rect.height > 0) {
            // Ne re-render que si les dimensions ont vraiment changé significativement (> 5px)
            const newDims = { width: rect.width, height: rect.height };
            const widthDiff = Math.abs(newDims.width - lastResizeDimsRef.current.width);
            const heightDiff = Math.abs(newDims.height - lastResizeDimsRef.current.height);
            
            if (widthDiff > 5 || heightDiff > 5) {
              setContainerDims(newDims);
              lastResizeDimsRef.current = newDims;
              // Ne forcer un re-render du graphique que si vraiment nécessaire
              setChartKey(prev => prev + 1);
            }
          }
        }, 500); // Augmenter le debounce à 500ms pour réduire les re-renders
      });
      
      resizeObserverRef.current.observe(containerRef.current);
      
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
      };
    }, [isVisible]);
    
    // Stabiliser les données avec useMemo - utiliser une clé basée sur le contenu réel
    const dataKey = useMemo(() => {
      if (!data || data.length === 0) return 'empty';
      return data.map(item => `${item.name}:${item.value}`).join('|');
    }, [data]);
    
    const stableData = useMemo(() => {
      // Logs désactivés pour performance
      
      if (!data || data.length === 0) {
        return null;
      }
      // Créer une copie stable des données
      const result = data.map(item => ({ ...item }));
      return result;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataKey, title]);
    
    // Calculer shouldRender une seule fois (après stableData)
    const shouldRender = useMemo(() => {
      return stableData && stableData.length > 0 && isVisible && containerDims.width > 0 && containerDims.height > 0;
    }, [stableData, isVisible, containerDims]);
    
    // Vérification SVG désactivée pour performance
    // useEffect(() => {
    //   if (shouldRender && containerRef.current && isVisible) {
    //     const timer = setTimeout(() => {
    //       // Vérification SVG
    //     }, 50);
    //     return () => clearTimeout(timer);
    //   }
    // }, [shouldRender, isVisible, chartKey, title]);

    if (!stableData || stableData.length === 0) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#666', fontFamily: '"Segoe UI", "Source Sans", sans-serif' }}>
          <h3 style={{ 
            marginBottom: '1rem', 
            fontSize: '1.1rem', 
            fontWeight: 500,
            fontFamily: '"Segoe UI", "Source Sans", sans-serif',
            color: 'rgb(29, 29, 31)'
          }}>
            {title}
          </h3>
          <p>Aucune donnée disponible</p>
        </div>
      );
    }

    const total = stableData.reduce((sum, item) => sum + item.value, 0);
    
    if (total === 0) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#666', fontFamily: '"Segoe UI", "Source Sans", sans-serif' }}>
          <h3 style={{ 
            marginBottom: '1rem', 
            fontSize: '1.1rem', 
            fontWeight: 500,
            fontFamily: '"Segoe UI", "Source Sans", sans-serif',
            color: 'rgb(29, 29, 31)'
          }}>
            {title}
          </h3>
          <p>Total des valeurs est 0</p>
        </div>
      );
    }
    
    // Logs de débogage désactivés

    const renderLabel = (entry) => {
      const percent = ((entry.value / total) * 100).toFixed(1);
      return `${entry.name}: ${percent}%`;
    };

    return (
      <div 
        ref={containerRef}
        style={{ 
          width: '100%', 
          height: '400px', 
          minHeight: '400px',
          fontFamily: '"Segoe UI", "Source Sans", sans-serif', 
          position: 'relative', 
          minWidth: 0,
          display: 'block',
          overflow: 'visible'
        }}
      >
        <h3 style={{ 
          marginBottom: '1rem', 
          fontSize: '1.1rem', 
          fontWeight: 500,
          fontFamily: '"Segoe UI", "Source Sans", sans-serif',
          color: 'rgb(29, 29, 31)'
        }}>
          {title}
        </h3>
        <div 
          style={{ 
            width: '100%', 
            height: '320px', 
            minWidth: '0', 
            minHeight: '320px', 
            position: 'relative',
            display: 'block',
            overflow: 'visible',
            backgroundColor: 'transparent',
            flexShrink: 0
          }}
        >
          {(() => {
            // Log désactivé pour performance
            
            if (!shouldRender) {
              // Afficher un message de chargement si les conditions ne sont pas remplies
              return (
                <div style={{ width: '100%', height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                  {process.env.NODE_ENV === 'development' && (
                    <div style={{ fontSize: '0.75rem', textAlign: 'center' }}>
                      <div>Attente des conditions de rendu...</div>
                      <div style={{ marginTop: '0.5rem', color: '#666' }}>
                        {!stableData ? 'Données non disponibles' : 
                         stableData.length === 0 ? 'Données vides' : 
                         !isVisible ? 'En attente de visibilité' : 
                         containerDims.width === 0 || containerDims.height === 0 ? `Dimensions: ${containerDims.width}x${containerDims.height}` : 
                         'État inconnu'}
                      </div>
                    </div>
                  )}
                  {process.env.NODE_ENV === 'production' && (
                    <span>Chargement du graphique...</span>
                  )}
                </div>
              );
            }
              
            // Log désactivé pour performance
              
            // Utiliser des dimensions fixes calculées depuis containerDims
            const chartWidth = containerDims.width > 0 ? containerDims.width - 40 : 600; // -40 pour padding
            const chartHeight = 320;
            
            return (
              <div style={{ width: '100%', height: '320px', minHeight: '320px', position: 'relative' }}>
                <ResponsiveContainer 
                  key={`chart-${chartKey}-${title}`} 
                  width={chartWidth}
                  height={chartHeight}
                  minWidth={300}
                  minHeight={320}
                  debounce={100}
                >
                  <PieChart width={chartWidth} height={chartHeight}>
                    <Pie
                      data={stableData}
                      cx={chartWidth / 2}
                      cy={chartHeight / 2}
                      labelLine={false}
                      label={renderLabel}
                      outerRadius={Math.min(chartWidth, chartHeight) * 0.3}
                      fill="#8884d8"
                      dataKey="value"
                      isAnimationActive={true}
                    >
                      {stableData.map((entry, index) => {
                        const color = entry.color || '#8884d8';
                        return <Cell key={`cell-${index}`} fill={color} />;
                      })}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [
                        formatCurrency(value, 'EUR', 0),
                        'Valeur'
                      ]}
                      contentStyle={{
                        fontFamily: '"Segoe UI", "Source Sans", sans-serif',
                        fontSize: '0.875rem',
                        border: '1px solid var(--border-color, rgb(237, 237, 237))',
                        borderRadius: '8px',
                        backgroundColor: 'var(--surface, white)',
                        padding: '0.5rem 0.75rem',
                        color: 'var(--text-primary, rgb(29, 29, 31))'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  const valorisationsColumns = [
    { key: 'date_valorisation', label: 'Date', align: 'left' },
    { key: 'valeur_totale', label: 'Valeur de la Position', align: 'right' },
  ];

  const formattedValorisations = useMemo(() => {
    if (!Array.isArray(valorisations)) return [];
    return valorisations.map((v) => {
      const rawDate = v?.date_valorisation;
      const dateStr = rawDate ? (() => {
        const d = new Date(rawDate);
        return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('fr-FR');
      })() : '-';
      const rawVal = v?.valeur_totale;
      const num = rawVal != null ? parseFloat(rawVal) : NaN;
      const valueStr = Number.isFinite(num) ? formatCurrency(num, 'EUR', 2) : '-';
      return {
        ...v,
        id: v.id,
        date_valorisation: dateStr,
        valeur_totale: valueStr,
      };
    });
  }, [valorisations]);

  const allTabs = [
    {
      tabId: 'portefeuille-synthese',
      label: 'Synthèse',
      content: (
        <div className="portefeuille-tab-content">
          <div style={{ marginBottom: '1.5rem' }}>
            <Title level={2}>Répartition du portefeuille</Title>
          </div>
          
          {loadingCategories ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0', padding: '2rem 0' }}>
              <Spinner text="Chargement des données..." size="medium" />
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem', minWidth: 0, width: '100%' }}>
              <div data-name="categorie">
                <PieChartComponent
                  data={portfolioDistributionData.byCategory}
                  title="Répartition par catégorie"
                />
              </div>
              <div data-name="fragilite">
                <PieChartComponent
                  data={portfolioDistributionData.byFragility}
                  title="Répartition par fragilité"
                />
              </div>
            </div>
          )}

          {/* Evolution de la valeur du portefeuille — même source que le tableau Valorisations (portfolio_valorisations), même mise en page que Compte */}
          <section className="portefeuille-evolution-section" data-name="evolution_position">
            <Title level={2}>Evolution de la valeur du portefeuille</Title>
            <div style={{ marginTop: '1.5rem' }}>
              {loadingValuationEvolution ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '2rem 0' }}>
                  <Spinner text="Chargement de l'évolution..." size="medium" />
                </div>
              ) : valuationEvolutionPortefeuille?.data && chartDataEvolutionPortefeuille.length > 0 ? (
                <div ref={evolutionChartContainerRef} className="compte-chart-container" style={{ height: '400px', minHeight: '400px', marginBottom: '1rem' }}>
                  {isEvolutionChartVisible && evolutionChartContainerDims.width > 0 && evolutionChartContainerDims.height > 0 ? (
                    <ResponsiveContainer key={`evolution-chart-${evolutionChartKey}`} width={evolutionChartContainerDims.width} height={evolutionChartContainerDims.height}>
                      <AreaChart data={chartDataEvolutionPortefeuille} margin={{ top: 10, right: 10, bottom: 5, left: 48 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) => new Date(value).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' })}
                          tick={{ style: { fontFamily: '"Segoe UI", "Source Sans", sans-serif', fontSize: 12 } }}
                        />
                        <YAxis
                          tickFormatter={(value) => (value / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' k' + getCurrencySymbol('EUR')}
                          tick={{ style: { fontFamily: '"Segoe UI", "Source Sans", sans-serif', fontSize: 12 } }}
                        />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            const valValeur = payload.find(p => p.dataKey === 'valeur')?.value ?? payload[0]?.value;
                            return (
                              <div className="compte-chart-tooltip">
                                <div style={{ marginBottom: '8px', fontWeight: 600 }}>{label ? new Date(label).toLocaleDateString('fr-FR') : ''}</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '24px' }}>
                                  <span style={{ textAlign: 'left' }}>Valeur</span>
                                  <span style={{ textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}>{formatCurrency(valValeur ?? 0, 'EUR', 0)}</span>
                                </div>
                              </div>
                            );
                          }}
                          wrapperStyle={{ fontFamily: '"Segoe UI", "Source Sans", sans-serif' }}
                        />
                        <Area
                          type="monotone"
                          dataKey="valeur"
                          stroke="#1f1f1f"
                          fill="#1f1f1f"
                          fillOpacity={0.1}
                          name="Valeur"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                      <span>Chargement du graphique...</span>
                    </div>
                  )}
                </div>
              ) : (
                <p>Aucune donnée de valorisation disponible pour l'évolution du portefeuille.</p>
              )}
            </div>
          </section>
        </div>
      )
    },
    {
      tabId: 'portefeuille-detail',
      label: 'Portefeuille',
      content: (
        <div className="portefeuille-tab-content">
          <div style={{ marginBottom: '1.5rem' }}>
            <Title level={2}>Détail du portefeuille</Title>
          </div>
          {loading && !backgroundLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0', padding: '2rem 0' }}>
              <Spinner text="Chargement des positions..." size="medium" />
            </div>
          ) : positions.length === 0 ? (
            <div>
              <p>Aucune position disponible</p>
              {isConnected && (
                <div style={{ marginTop: '12px' }}>
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    Vérifiez que vous avez des positions ouvertes dans votre compte IBKR et que IB Gateway est bien connecté.
                  </p>
                      <button
                        onClick={loadPositions}
                        disabled={loading}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f0f2f6',
                      border: '1px solid #d0d4da',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      color: 'rgb(29, 29, 31)'
                    }}
                  >
                        {loading ? 'Rechargement...' : 'Recharger les positions'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="portefeuille-table-wrapper">
            <Table
              name="portefeuille"
              keyColumn="symbol"
              columns={positionsColumns}
              data={positions}
              striped={true}
              hover={true}
              onRowClick={onActifClick}
                defaultSortColumn="symbol"
                defaultSortDirection="asc"
            />
            </div>
          )}
        </div>
      )
    },
    {
      tabId: 'portefeuille-performance',
      label: 'Performance',
      content: (
        <div className="portefeuille-tab-content">
          <p>Performance du portefeuille</p>
        </div>
      )
    },
    {
      tabId: 'portefeuille-volatilite',
      label: 'Volatilité',
      content: (
        <div className="portefeuille-tab-content">
          <p>Analyse de la volatilité</p>
        </div>
      )
    },
    {
      tabId: 'portefeuille-sentiment',
      label: 'Sentiment',
      content: (
        <div className="portefeuille-tab-content">
          <p>Analyse du sentiment</p>
        </div>
      )
    },
    {
      tabId: 'portefeuille-ordres',
      label: 'Ordres',
      content: (
        <div className="portefeuille-tab-content">
          <div style={{ marginBottom: '1.5rem' }}>
            <Title level={2}>Ordres en cours</Title>
          </div>
          {loadingOrders ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0', padding: '2rem 0' }}>
              <Spinner text="Chargement des ordres..." size="medium" />
            </div>
          ) : orders.length === 0 ? (
            <div>
              <p>Aucun ordre en cours</p>
              {isConnected ? (
                <div style={{ marginTop: '12px' }}>
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    {loadingOrders 
                      ? 'Chargement en cours...' 
                      : 'Aucun ordre ouvert trouvé dans votre compte IBKR. Les ordres remplis ou annulés ne sont pas affichés ici.'}
                  </p>
                  <button
                    onClick={loadOrders}
                    disabled={loadingOrders}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f0f2f6',
                      border: '1px solid #d0d4da',
                      borderRadius: '8px',
                      cursor: loadingOrders ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      color: 'rgb(29, 29, 31)'
                    }}
                  >
                    {loadingOrders ? 'Rechargement...' : 'Recharger les ordres'}
                  </button>
                </div>
              ) : (
                <div style={{ marginTop: '12px' }}>
                  <p style={{ fontSize: '12px', color: '#dc3545', marginBottom: '8px' }}>
                    IB Gateway n'est pas connecté. Veuillez vous connecter depuis Paramètres {'>'} API.
                  </p>
                </div>
              )}
            </div>
          ) : (
                <Table
                  columns={ordersColumns}
                  data={orders}
                  striped={true}
                  hover={true}
                  defaultSortColumn="symbol"
                  defaultSortDirection="asc"
                />
          )}
        </div>
      )
    },
    {
      tabId: 'portefeuille-valorisation',
      label: 'Valorisations',
      content: (
        <div className="portefeuille-tab-content portefeuille-tab-content-valorisations">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
            <Title level={2}>Valorisations</Title>
            <Button
              variant="secondary"
              onClick={() => {
                setEditingValorisation(null);
                setValorisationForm({
                  date_valorisation: new Date().toISOString().split('T')[0],
                  valeur_totale: '',
                });
                setShowAddValorisationModal(true);
              }}
              style={{ minWidth: '100px' }}
            >
              Ajouter
            </Button>
          </div>

          {loadingValorisations ? (
            <Spinner />
          ) : formattedValorisations.length === 0 ? (
            <p className="bpm-table-empty-state" style={{ padding: '1.5rem', color: 'var(--text-secondary, #666)', textAlign: 'center' }}>
              Aucune valorisation enregistrée. Utilisez « Ajouter » pour saisir une valeur de position.
            </p>
          ) : (
            <Table
              name="valorisations"
              columns={valorisationsColumns}
              data={formattedValorisations}
              striped={true}
              hover={true}
              onRowClick={(row) => {
                const val = valorisations.find((v) => v.id === row.id);
                if (val) {
                  setEditingValorisation(val);
                  setValorisationForm({
                    date_valorisation: val.date_valorisation,
                    valeur_totale: String(val.valeur_totale ?? ''),
                  });
                  setShowEditValorisationModal(true);
                }
              }}
              defaultSortColumn="date_valorisation"
              defaultSortDirection="desc"
            />
          )}

          <Modal
            isOpen={showAddValorisationModal || showEditValorisationModal}
            onClose={() => {
              setShowAddValorisationModal(false);
              setShowEditValorisationModal(false);
              setEditingValorisation(null);
              setValorisationForm({
                date_valorisation: new Date().toISOString().split('T')[0],
                valeur_totale: '',
              });
            }}
            title={editingValorisation ? 'Modifier une valorisation' : 'Ajouter une valorisation'}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <DateInput
                label="Date"
                value={valorisationForm.date_valorisation ? new Date(valorisationForm.date_valorisation) : null}
                onChange={(date) => setValorisationForm({ ...valorisationForm, date_valorisation: date ? date.toISOString().split('T')[0] : '' })}
              />
              <NumberInput
                label="Valeur de la Position"
                value={valorisationForm.valeur_totale}
                onChange={(val) => setValorisationForm({ ...valorisationForm, valeur_totale: val === null || val === undefined ? '' : String(val) })}
              />
              <div className="form-actions compte-modal-actions">
                {editingValorisation && (
                  <Button
                    variant="secondary"
                    onClick={() => handleDeleteValorisation(editingValorisation)}
                    style={{ backgroundColor: '#dc3545', color: 'white' }}
                  >
                    Supprimer
                  </Button>
                )}
                <Button variant="primary" onClick={handleSaveValorisation} disabled={!valorisationForm.valeur_totale}>
                  {editingValorisation ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      )
    }
  ];

  // Filtrer les onglets selon allowed_tabs (BPM tabs)
  const tabs = allTabs.filter(tab => canAccessPortefeuilleTab(tab.tabId));

  // Réajuster activeTab si l'onglet actuel n'est plus accessible
  useEffect(() => {
    if (activeTab >= tabs.length && tabs.length > 0) {
      setActiveTab(0);
    }
  }, [activeTab, tabs.length]);

  // Charger les valorisations quand on ouvre l'onglet Valorisations (GET /portfolio/valuations = Valeur de la Position, pas valeur-nette)
  const portefeuilleTabIds = useMemo(() => [
    'portefeuille-synthese', 'portefeuille-detail', 'portefeuille-performance',
    'portefeuille-volatilite', 'portefeuille-sentiment', 'portefeuille-ordres', 'portefeuille-valorisation'
  ], []);
  useEffect(() => {
    const allowed = portefeuilleTabIds.filter((id) => canAccessPortefeuilleTab(id));
    const currentId = allowed[activeTab];
    if (currentId === 'portefeuille-valorisation') {
      loadValorisations();
    }
  }, [activeTab, portefeuilleTabIds, canAccessPortefeuilleTab, loadValorisations]);

  // Référence pour le carrousel
  const carouselRef = useRef(null);

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 300;
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="portefeuille-page">
      <div className="metrics-carousel-container">
        {!isConnected ? (
          <div className="metrics-carousel metrics-carousel-loading" aria-busy="false">
            <span className="metrics-loading-label">Connectez-vous pour voir les métriques</span>
          </div>
        ) : accountForMetrics ? (
            <>
              <button 
                className="carousel-btn left" 
                onClick={() => scrollCarousel('left')}
                aria-label="Défiler vers la gauche"
              >
                ←
              </button>
              <div className="metrics-carousel" ref={carouselRef}>
                {metricsConfig.map((m, idx) => (
                  <div className="metric-card" key={idx}>
                    <Metric label={m.label} value={m.getValue(accountForMetrics)} help={m.help} />
                  </div>
                ))}
              </div>
              <button 
                className="carousel-btn right" 
                onClick={() => scrollCarousel('right')}
                aria-label="Défiler vers la droite"
              >
                →
              </button>
            </>
          ) : (
            <div className="metrics-carousel metrics-carousel-loading" aria-busy="true">
              <Spinner text="Chargement des métriques…" />
            </div>
          )}
      </div>
      <Tabs 
        tabs={tabs}
        defaultTab={activeTab}
        onChange={setActiveTab}
      />
    </div>
  );
};

export default Portefeuille;

